import React, { useMemo, useState } from 'react';
import { Bell, X, Check, Clock, AlertCircle, Calendar, CheckCircle, Trash2, RefreshCw } from 'lucide-react';
import { NotificationItem } from './useNotificationFeed';

interface NotificationCenterProps {
  isOpen: boolean;
  notifications: NotificationItem[];
  loading?: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onDelete: (id: string) => void;
}

const formatTimeAgo = (iso: string) => {
  const created = new Date(iso);
  const now = new Date();
  const diffMinutes = Math.floor((now.getTime() - created.getTime()) / (1000 * 60));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
  return `${Math.floor(diffMinutes / 1440)}d ago`;
};

const typeToIcon = (type?: string) => {
  switch (type) {
    case 'task_overdue':
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    case 'task_due_soon':
      return <Clock className="w-5 h-5 text-yellow-500" />;
    case 'task_completed':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    default:
      return <Bell className="w-5 h-5 text-gray-400" />;
  }
};

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  isOpen,
  notifications,
  loading = false,
  onClose,
  onRefresh,
  onMarkRead,
  onMarkAllRead,
  onDelete,
}) => {
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const filtered = useMemo(() => {
    if (filter === 'unread') {
      return notifications.filter((n) => !n.read);
    }
    return notifications;
  }, [filter, notifications]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-gray-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
              <p className="text-sm text-gray-600">Stay on top of updates across your account</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onRefresh}
              className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllRead}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              title="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex border-b border-gray-200 px-6">
          {[
            { key: 'all', label: 'All' },
            { key: 'unread', label: `Unread (${unreadCount})` },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key as 'all' | 'unread')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                filter === key ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="max-h-[65vh] overflow-y-auto divide-y divide-gray-100">
          {loading && (
            <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
              Loading notifications...
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="py-10 px-6 text-center text-gray-500">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700">No notifications yet</p>
              <p className="text-sm text-gray-500">You will see updates here when they arrive.</p>
            </div>
          )}

          {filtered.map((notification) => (
            <div key={notification.id} className={`px-6 py-4 flex gap-4 ${notification.read ? 'bg-white' : 'bg-primary-50'}`}>
              <div className="pt-1">{typeToIcon(notification.type)}</div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{notification.title}</p>
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{notification.message}</p>
                    <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                      <span>{formatTimeAgo(notification.createdAt)}</span>
                      {!notification.read && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                          New
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!notification.read && (
                      <button
                        onClick={() => onMarkRead(notification.id)}
                        className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                      >
                        <Check className="w-4 h-4" />
                        Mark read
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(notification.id)}
                      className="text-xs text-gray-500 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
